import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  TextField,
  IconButton,
  Chip,
  Tooltip,
  Alert,
  Snackbar,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControlLabel,
  Switch,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import { Add, Edit, Delete, Search, Inventory2, Save } from '@mui/icons-material';
import apiService from '../services/api.service';
import ResponsiveTable, { ResponsiveTableColumn } from '../components/common/ResponsiveTable';
import ResponsiveDialog from '../components/common/ResponsiveDialog';

interface SeedRow {
  seedId: number;
  itemNo: string;
  itemDescription: string;
  itemGroup: string;
  pic: string;
  barcode: string;
  gtin: string;
  isActive: boolean;
  updatedDate: string | null;
}

const EMPTY: Omit<SeedRow, 'seedId' | 'updatedDate'> = {
  itemNo: '', itemDescription: '', itemGroup: '', pic: '', barcode: '', gtin: '', isActive: true,
};

type Toast = { severity: 'success' | 'error' | 'info'; message: string } | null;

/**
 * Item Seed — maintain the company-independent fallback list used to resolve a
 * scanned barcode/GTIN to an item when the live SAP sync (sap-items / OBCD) and
 * the captured barcode map have no match. This is the last resort in the
 * Warehouse Receiving resolution chain.
 */
export default function WarehouseItemSeed(): JSX.Element {
  const [rows, setRows] = useState<SeedRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<SeedRow | null>(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<Toast>(null);

  const load = useCallback(async (s?: string) => {
    setLoading(true);
    try {
      const res: any = await apiService.listItemSeed(s);
      setRows(Array.isArray(res?.data) ? res.data : []);
    } catch (e: any) {
      setToast({ severity: 'error', message: e?.response?.data?.message ?? 'Failed to load item seed' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Debounced search.
  useEffect(() => {
    const t = setTimeout(() => void load(search.trim() || undefined), 300);
    return () => clearTimeout(t);
  }, [search, load]);

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY);
    setDialogOpen(true);
  };
  const openEdit = (r: SeedRow) => {
    setEditing(r);
    setForm({ itemNo: r.itemNo, itemDescription: r.itemDescription, itemGroup: r.itemGroup, pic: r.pic, barcode: r.barcode, gtin: r.gtin, isActive: r.isActive });
    setDialogOpen(true);
  };

  const save = useCallback(async () => {
    if (!form.itemNo.trim()) {
      setToast({ severity: 'error', message: 'Item No. is required' });
      return;
    }
    setSaving(true);
    try {
      if (editing) await apiService.updateItemSeed(editing.seedId, form);
      else await apiService.createItemSeed(form);
      setToast({ severity: 'success', message: editing ? 'Item updated' : 'Item added' });
      setDialogOpen(false);
      await load(search.trim() || undefined);
    } catch (e: any) {
      setToast({ severity: 'error', message: e?.response?.data?.message ?? 'Save failed' });
    } finally {
      setSaving(false);
    }
  }, [form, editing, load, search]);

  const remove = useCallback(
    async (r: SeedRow) => {
      if (!window.confirm(`Delete seed item ${r.itemNo}?`)) return;
      try {
        await apiService.deleteItemSeed(r.seedId);
        setToast({ severity: 'info', message: 'Item deleted' });
        await load(search.trim() || undefined);
      } catch (e: any) {
        setToast({ severity: 'error', message: e?.response?.data?.message ?? 'Delete failed' });
      }
    },
    [load, search]
  );

  const field = (key: keyof typeof form, label: string, opts: { mono?: boolean; required?: boolean } = {}) => (
    <TextField
      fullWidth
      size="small"
      label={label}
      required={opts.required}
      value={form[key] as string}
      onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
      InputProps={opts.mono ? { sx: { fontFamily: 'monospace' } } : undefined}
    />
  );

  const columns: ResponsiveTableColumn<SeedRow>[] = useMemo(
    () => [
      { id: 'itemNo', header: 'Item No.', cell: (r) => <strong>{r.itemNo}</strong> },
      { id: 'desc', header: 'Description', cell: (r) => r.itemDescription || '—' },
      { id: 'group', header: 'Item Group', cell: (r) => r.itemGroup || '—', hideOnMobile: true },
      { id: 'barcode', header: 'Barcode', cell: (r) => <span style={{ fontFamily: 'monospace' }}>{r.barcode || '—'}</span> },
      { id: 'gtin', header: 'GTIN', cell: (r) => <span style={{ fontFamily: 'monospace' }}>{r.gtin || '—'}</span> },
      { id: 'pic', header: 'PIC', cell: (r) => r.pic || '—', hideOnMobile: true },
      { id: 'active', header: 'Active', cell: (r) => (r.isActive ? <Chip size="small" color="success" label="Active" /> : <Chip size="small" label="Off" />) },
      {
        id: 'actions',
        header: 'Actions',
        align: 'right',
        cell: (r) => (
          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => openEdit(r)} aria-label="Edit"><Edit fontSize="small" /></IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton size="small" color="error" onClick={() => remove(r)} aria-label="Delete"><Delete fontSize="small" /></IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [remove]
  );

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
        <Inventory2 color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Item Seed
        </Typography>
        <Chip size="small" label={`${rows.length} items`} />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Company-independent fallback list. Used to resolve a scanned barcode/GTIN to an item only when the live SAP
        sync and the captured barcode map have no match (the last resort).
      </Typography>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }} alignItems={{ sm: 'center' }}>
          <TextField
            size="small"
            placeholder="Search item, barcode, GTIN…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1, minWidth: 220 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
          />
          {loading && <CircularProgress size={20} />}
          <Box sx={{ flex: 1 }} />
          <Button variant="contained" startIcon={<Add />} onClick={openAdd}>
            Add item
          </Button>
        </Stack>
        <ResponsiveTable
          ariaLabel="Item seed list"
          columns={columns}
          rows={rows}
          getRowKey={(r) => r.seedId}
          emptyMessage="No seed items. Add one to begin."
          size="small"
        />
      </Paper>

      <ResponsiveDialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? `Edit ${editing.itemNo}` : 'Add seed item'}</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            A scanned 1D barcode or DataMatrix GTIN matching either field below resolves to this item.
          </Alert>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={4}>{field('itemNo', 'Item No.', { required: true })}</Grid>
            <Grid item xs={12} sm={8}>{field('itemDescription', 'Item Description')}</Grid>
            <Grid item xs={12} sm={6}>{field('itemGroup', 'Item Group')}</Grid>
            <Grid item xs={12} sm={6}>{field('pic', 'PIC')}</Grid>
            <Grid item xs={12} sm={6}>{field('barcode', 'Barcode (1D)', { mono: true })}</Grid>
            <Grid item xs={12} sm={6}>{field('gtin', 'GTIN', { mono: true })}</Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={form.isActive} onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))} />}
                label="Active (used for resolution)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" startIcon={saving ? <CircularProgress size={16} /> : <Save />} onClick={save} disabled={saving}>
            {editing ? 'Save' : 'Add'}
          </Button>
        </DialogActions>
      </ResponsiveDialog>

      <Snackbar open={!!toast} autoHideDuration={3500} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <Alert severity={toast.severity} onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast.message}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
}
